'use client';

import { useState, useEffect, useCallback } from 'react';
import { startOfDay } from 'date-fns';
import { getMobileWeekStart, getWeekStart } from '@/helpers/class';
import { useDeviceType } from '@/hooks/use-device-type';

interface DateFilterState {
  selectedDate: Date | null;
  currentWeekStart: Date | null;
  selectedClass: string;
  selectedInstructor: string;
  isClosestClassNavigation: boolean;
  isRestoredFromStorage: boolean;
}

export const DATE_FILTER_STORAGE_KEY = 'mokoboko_daily_filters';

const getDefaultState = (deviceType: string): DateFilterState => {
  const today = startOfDay(new Date());
  return {
    selectedDate: today,
    currentWeekStart:
      deviceType === 'mobile' ? getMobileWeekStart(today) : getWeekStart(today),
    selectedClass: 'all',
    selectedInstructor: 'all',
    isClosestClassNavigation: false,
    isRestoredFromStorage: false,
  };
};

export const useDateFilter = () => {
  const deviceType = useDeviceType();
  const [state, setState] = useState<DateFilterState>(() => {
    return getDefaultState(deviceType);
  });
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    try {
      const savedState = sessionStorage.getItem(DATE_FILTER_STORAGE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        const restoredState = {
          ...parsedState,
          selectedDate: parsedState.selectedDate
            ? new Date(parsedState.selectedDate)
            : null,
          currentWeekStart: parsedState.currentWeekStart
            ? new Date(parsedState.currentWeekStart)
            : null,
          isRestoredFromStorage: true,
        };
        setState(restoredState);
      }
    } catch (error) {
      console.error('Error loading date filter state:', error);
      sessionStorage.removeItem(DATE_FILTER_STORAGE_KEY);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (isInitialized) {
      try {
        sessionStorage.setItem(DATE_FILTER_STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        console.error('Error saving date filter state:', error);
      }
    }
  }, [state, isInitialized]);

  // Base the recalculation on the selected date (or today) instead of the previous week start
  useEffect(() => {
    if (isInitialized) {
      const baseDate = state.selectedDate ?? startOfDay(new Date());
      const newWeekStart =
        deviceType === 'mobile'
          ? getMobileWeekStart(baseDate)
          : getWeekStart(baseDate);

      setState((prev) => ({
        ...prev,
        currentWeekStart: newWeekStart,
      }));
    }
  }, [deviceType, isInitialized]);

  const updateState = useCallback((newState: Partial<DateFilterState>) => {
    setState((prev) => ({ ...prev, ...newState }));
  }, []);

  const setSelectedDate = useCallback((date: Date | null) => {
    setState((prev) => ({ ...prev, selectedDate: date }));
  }, []);

  const setCurrentWeekStart = useCallback((date: Date | null) => {
    setState((prev) => ({ ...prev, currentWeekStart: date }));
  }, []);

  const setSelectedClass = useCallback((classId: string) => {
    setState((prev) => ({ ...prev, selectedClass: classId }));
  }, []);

  const setSelectedInstructor = useCallback((instructorId: string) => {
    setState((prev) => ({ ...prev, selectedInstructor: instructorId }));
  }, []);

  const setIsClosestClassNavigation = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, isClosestClassNavigation: value }));
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaultState = getDefaultState(deviceType);
    setState(defaultState);
  }, [deviceType]);

  const clearStoredState = useCallback(() => {
    sessionStorage.removeItem(DATE_FILTER_STORAGE_KEY);
    resetToDefaults();
  }, [resetToDefaults]);

  const markAsNotRestored = useCallback(() => {
    setState((prev) => ({ ...prev, isRestoredFromStorage: false }));
  }, []);

  return {
    state,
    isInitialized,
    updateState,
    setSelectedDate,
    setCurrentWeekStart,
    setSelectedClass,
    setSelectedInstructor,
    setIsClosestClassNavigation,
    resetToDefaults,
    clearStoredState,
    markAsNotRestored,
  };
};
